import type {
  CategoryRange,
  CellStats,
  EnemyCategory,
  EnemyTier,
  PathogenDef,
  PathogenKind,
  RegionDef,
} from "./types";

// ─────────────────────────────────────────────────────────────────────────────
// Central tuning surface. TWO gameplay modes (chosen per region):
//   engulf (Lungs)  — eat weaker bacteria to grow; equal/stronger → breathe.
//   shoot  (Intestines) — auto-fire; deplete enemy HP; enemies fire back; health.
// Static maps — clear every enemy to win.
// ─────────────────────────────────────────────────────────────────────────────

/** Screen (viewport) size. Portrait, one-thumb. Scaled with FIT. */
export const GAME_WIDTH = 720;
export const GAME_HEIGHT = 1280;

export const COLORS = {
  bgTop: 0x0a1530,
  bgBottom: 0x123a5e,
  cell: 0xeaf6ff,
  cellCore: 0x8fd0ff,
  shot: 0xffffff,
  enemyShot: 0xff7a4d,
  primaryPickup: 0xff6b6b,
  secondaryPickup: 0x5ec8ff,
  danger: 0xff5d73,
  safe: 0x6bf0a4,
  enemyRing: 0xff8e8e,
  sparkle: 0xfff4c2,
  inflamed: 0xc0303a,
  affirm: 0xbdf0d0,
  gold: 0xffd86b,
  rapid: 0x9fe8ff,
  shield: 0x9fe8ff,
} as const;

// ── Player cell ──────────────────────────────────────────────────────────────
export const NEUTROPHIL: CellStats = {
  id: "neutrophil",
  name: "Neutrophil",
  moveSpeed: 340,
  radius: 40,
  startingStrength: 10,
  startingPower: 1,
  maxHealth: 100,
};

export const PAUSE_BREATHE_BONUS = 1; // +1 strength (engulf) or +1 power (shoot)
export const MISSION_COMPLETE_BONUS = 100;

// ── Enemy value scaling (fractions of the base below) ────────────────────────
export const ENEMY_VALUE_BASE = 10;
export const CATEGORY_RANGES: Record<EnemyCategory, CategoryRange> = {
  weak: { min: 0.3, max: 0.7 },
  matched: { min: 0.8, max: 1.1 },
  threat: { min: 1.2, max: 1.8 },
};
export const ELITE_RANGE: CategoryRange = { min: 1.0, max: 1.6 };
export const APEX_RANGE: CategoryRange = { min: 2.0, max: 3.0 };

// ── ENGULF mode (Lungs) ──────────────────────────────────────────────────────
/** Movement speed multiplier while the digestion animation plays. */
export const DIGESTION_LOCKOUT_SPEED = 0.65;
/** Digestion animation duration (ms) — gates how fast you can chain engulfs. */
export const DIGEST_MS = 850;
/** Final-approach tractor window — abort an engulf by swiping away (ms). */
export const TRACTOR_MS = 200;
/** Extra reach (px) added to body radii when deciding engulf range. */
export const ENGULF_PAD = 8;
/** Grace after a revive where the cell can't engulf (prevents death loops). */
export const ENGULF_GRACE_MS = 1200;
/** Flat Strength gained per successful engulf, by tier. */
export const STRENGTH_GAIN: Record<EnemyTier, number> = { standard: 1, elite: 2, apex: 3 };
/** Combo multiplier by consecutive-engulf count (within the window). */
export const COMBO_WINDOW_MS = 3000;
export const COMBO_MULTS = [1, 1.5, 2, 3] as const;
/** Engulf pickups: red = +Strength, blue = shield (next fatal engulf → win). */
export const STRENGTH_BOOST_AMOUNT = 2;
export const SHIELD_DURATION_MS = 10000;

// ── SHOOT mode (Intestines) ──────────────────────────────────────────────────
export const FIRE_INTERVAL_MS = 300;
export const FIRE_RANGE = 380;
export const SHOT_SPEED = 660;
export const SHOT_LIFESPAN_MS = 1400;
export const POP_MS = 320;
/** Red = +power (permanent); blue = rapid fire window. */
export const POWER_PICKUP_AMOUNT = 1;
export const RAPID_FIRE_MS = 10000;
export const RAPID_FIRE_MULT = 2;
/** Player health & invulnerability (shoot mode). */
export const PLAYER_IFRAMES_MS = 600;
export const REVIVE_INVULN_MS = 2000;
/** Enemies fire back — kept MODEST via a global volley cap. */
export const ENEMY_FIRE_RANGE = 470;
export const ENEMY_VOLLEY_MS = 1100;
export const ENEMY_SHOTS_PER_VOLLEY_MAX = 1;
export const ENEMY_SHOT_SPEED = 300;
export const ENEMY_SHOT_DAMAGE = 8;
export const ENEMY_SHOT_LIFESPAN_MS = 2600;

// ── Pathogen visuals (look only — value assigned per enemy at placement) ─────
export const PATHOGENS: Record<PathogenKind, PathogenDef> = {
  virus: { kind: "virus", name: "Rhino-spikey", radius: 24, color: 0xb98cff, dropChance: 0.05 },
  bacterium: { kind: "bacterium", name: "Rod Bob", radius: 30, color: 0x7be09a, dropChance: 0.12 },
  toxinCloud: { kind: "toxinCloud", name: "Fumey", radius: 38, color: 0xff8e6b, dropChance: 0.2 },
  elite: { kind: "elite", name: "Elite Spiker", radius: 34, color: 0xff5bd0, dropChance: 0.35 },
  apex: { kind: "apex", name: "Hulking Mass", radius: 64, color: 0xff5d73, dropChance: 0.5 },
};

export const CATEGORY_KIND: Record<EnemyCategory, PathogenKind> = {
  weak: "virus",
  matched: "bacterium",
  threat: "toxinCloud",
};

// ── Regions (mode + static map + fixed population) ───────────────────────────
export const LUNGS_REGION: RegionDef = {
  name: "Lungs",
  mode: "engulf",
  map: { width: GAME_WIDTH * 2, height: GAME_HEIGHT * 2 },
  weak: 20,
  matched: 8,
  threat: 4,
  elite: 5,
  apex: 3,
};

export const INTESTINES_REGION: RegionDef = {
  name: "Intestines",
  mode: "shoot",
  map: { width: Math.round(GAME_WIDTH * 1.73), height: Math.round(GAME_HEIGHT * 1.73) },
  weak: 18,
  matched: 8,
  threat: 4,
  elite: 4,
  apex: 3,
};

export const REGIONS: Record<string, RegionDef> = {
  lungs: LUNGS_REGION,
  intestines: INTESTINES_REGION,
};
